interface DriverData {
  contextOfPurpose: string;
  currentState: string;
  problemOpportunity: string;
  ramifications: string;
  narrative: string;
}

export function exportDriver(driver: DriverData, format: 'markdown' | 'word' | 'pdf') {
  const markdown = generateMarkdown(driver);

  if (format === 'markdown') {
    downloadMarkdown(markdown);
  } else if (format === 'word') {
    // For Word, we'll create a simple HTML that can be opened in Word
    downloadWord(markdown);
  } else if (format === 'pdf') {
    // For PDF, we'll use the browser's print functionality
    downloadPDF(markdown);
  }
}

function generateMarkdown(driver: DriverData): string {
  let md = '# Outcome Driver\n\n';
  
  if (driver.contextOfPurpose) {
    md += `## Context of Purpose\n\n${driver.contextOfPurpose}\n\n`;
  }
  
  md += `## Outcome Driver Narrative\n\n${driver.narrative}\n\n`;
  
  md += `## Components\n\n`;
  md += `### Current State\n\n${driver.currentState}\n\n`;
  md += `### Problem / Opportunity\n\n${driver.problemOpportunity}\n\n`;
  md += `### Ramifications / Value Judgment\n\n${driver.ramifications}\n\n`;
  
  md += `---\n\n*Generated on ${new Date().toLocaleDateString()}*\n`;
  
  return md;
}

function downloadMarkdown(markdown: string) {
  const blob = new Blob([markdown], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `outcome-driver-${Date.now()}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function downloadWord(markdown: string) {
  // Convert markdown to HTML
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body { font-family: Arial, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; }
    h1 { color: #1a1a1a; border-bottom: 2px solid #2563eb; padding-bottom: 10px; }
    h2 { color: #374151; margin-top: 30px; }
    h3 { color: #4b5563; margin-top: 20px; }
    p { line-height: 1.6; color: #1f2937; }
  </style>
</head>
<body>
${markdownToHTML(markdown)}
</body>
</html>
  `;
  
  const blob = new Blob([html], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `outcome-driver-${Date.now()}.doc`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function downloadPDF(markdown: string) {
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    @media print {
      body { margin: 0; }
    }
    body { font-family: Arial, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; }
    h1 { color: #1a1a1a; border-bottom: 2px solid #2563eb; padding-bottom: 10px; }
    h2 { color: #374151; margin-top: 30px; }
    h3 { color: #4b5563; margin-top: 20px; }
    p { line-height: 1.6; color: #1f2937; }
  </style>
</head>
<body>
${markdownToHTML(markdown)}
</body>
</html>
  `;
  
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  }
}

function markdownToHTML(markdown: string): string {
  let html = markdown
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^---$/gm, '<hr>');
  
  // Convert paragraphs
  html = html.split('\n\n').map(para => {
    para = para.trim();
    if (!para) return '';
    if (para.startsWith('<h') || para.startsWith('<hr')) return para;
    return `<p>${para}</p>`;
  }).join('\n');
  
  return html;
}

